/**
 * The Production Records timeline (PROD-1279 part two): ONE card per
 * campus-day (`DayCard`), laid out one of two ways.
 *
 *   - `SingleCampusTimeline`: a flat list of day cards, newest first. The
 *     card's identity is the DAY, titled "Today" / "Yesterday" when it is
 *     one of those, else "Thu, Sep 17"; no day headers, no rail: the cards
 *     are the timeline.
 *   - `MultiCampusTimeline`: a dated rail. Each day is a header (a dot in the
 *     day's tone, the `TODAY · FRI · SEP 18` label) with one card per campus
 *     hanging off it; the card's identity is the CAMPUS. This replaces the
 *     campus tabs. The rollup ("2 campuses incomplete") is the section's
 *     accessible name only: the cards under the header already say it, so
 *     the chip that repeated it was removed (2026-09-21 review).
 *
 * Both hang each day off a DOM id (`daySectionId`) so the calendar rail can
 * scroll the list to a clicked day, and both hand the page the same
 * callbacks, always with the campus the card is for.
 *
 * Bespoke rather than `@ordo/ui/event-timeline`: that primitive groups
 * ITEMS by date and cannot badge a day or render a card with its own
 * actions. The dot + rail + card geometry matches it so the page still
 * reads like the other Ordo timelines.
 */

import { cn } from '@ordo/ui/cn'

import type { ProductionRecordDayWire } from '@/api/production-records'
import { DayCard } from '@/components/production/day-card'
import {
  type CampusDayGroup,
  type CampusRef,
  dayGroupStatus,
  type DayProgress,
  firstRecordId,
  mealtimesLine,
  signedLine,
} from '@/lib/production-records/day-progress'
import { daySectionId, formatDayGroupLabel, formatTaskDate, relativeDayLabel } from '@/lib/production-records/day-tasks'

export interface TimelineActions {
  /** The card's parts, percent and action, with the page's local drafts folded in. */
  progressFor: (locationId: string, day: ProductionRecordDayWire) => DayProgress
  /** Get started / Continue: the unified record for that campus-day. */
  onOpen: (locationId: string, day: ProductionRecordDayWire) => void
  /** View details: the record page for that campus-day (its first record; the page shows the rest as tabs). */
  onView: (locationId: string, recordId: string) => void
  onPrint: (locationId: string, serviceDate: string) => void
  now?: Date
}

export interface SingleCampusTimelineProps extends TimelineActions {
  campus: CampusRef
  days: ReadonlyArray<ProductionRecordDayWire>
}

export function SingleCampusTimeline({
  campus,
  days,
  progressFor,
  onOpen,
  onView,
  onPrint,
  now = new Date(),
}: SingleCampusTimelineProps) {
  return (
    <div className="space-y-2.5" data-layout="single">
      {days.map((day) => {
        const recordId = firstRecordId(day)
        return (
          <DayCard
            key={day.serviceDate}
            id={daySectionId(day.serviceDate)}
            title={relativeDayLabel(day.serviceDate, now) ?? formatTaskDate(day.serviceDate)}
            progress={progressFor(campus.id, day)}
            mealtimesLine={mealtimesLine(day)}
            signedLine={signedLine(day, now)}
            onOpen={() => onOpen(campus.id, day)}
            onView={recordId ? () => onView(campus.id, recordId) : null}
            onPrint={() => onPrint(campus.id, day.serviceDate)}
          />
        )
      })}
    </div>
  )
}

export interface MultiCampusTimelineProps extends TimelineActions {
  groups: ReadonlyArray<CampusDayGroup>
}

export function MultiCampusTimeline({
  groups,
  progressFor,
  onOpen,
  onView,
  onPrint,
  now = new Date(),
}: MultiCampusTimelineProps) {
  return (
    <div className="space-y-6" data-layout="multi">
      {groups.map((group) => {
        const label = formatDayGroupLabel(group.serviceDate, now)
        const status = dayGroupStatus(group)
        const complete = status.tone === 'complete'
        return (
          <section
            key={group.serviceDate}
            id={daySectionId(group.serviceDate)}
            aria-label={`${label}, ${status.label.toLowerCase()}`}>
            <div className="mb-2.5 flex items-center gap-2 px-1">
              <span
                className={cn('h-2.5 w-2.5 shrink-0 rounded-full', complete ? 'bg-emerald-600' : 'bg-orange-600')}
                aria-hidden
              />
              <h2 className="text-xs font-semibold tracking-wide text-foreground">{label}</h2>
            </div>
            <div
              className={cn(
                'ml-1 space-y-2.5 border-l-2 pl-5',
                complete ? 'border-emerald-600/20' : 'border-orange-600/20',
              )}>
              {group.rows.map(({ campus, day }) => {
                const recordId = firstRecordId(day)
                return (
                  <DayCard
                    key={campus.id}
                    dense
                    title={campus.name}
                    progress={progressFor(campus.id, day)}
                    mealtimesLine={mealtimesLine(day)}
                    signedLine={signedLine(day, now)}
                    onOpen={() => onOpen(campus.id, day)}
                    onView={recordId ? () => onView(campus.id, recordId) : null}
                    onPrint={() => onPrint(campus.id, day.serviceDate)}
                  />
                )
              })}
            </div>
          </section>
        )
      })}
    </div>
  )
}
